import { execute } from '@/graphql/execute';
import type { FeaturesQuery } from '@/graphql/graphql';
import { featuresByLayerQuery } from '@/queries/feature.query';
import { createOfflineStorage } from './createOfflineStorage';
import { getSnapshot } from './snapshotStore';

/**
 * A single feature as returned by `featuresByLayerQuery` (and snapshotted by
 * `featuresByProjectQuery`, which selects the same fields). The two queries are
 * field-compatible so the snapshot is interchangeable with a live response.
 */
export type OfflineFeature = FeaturesQuery['featuresByLayer'][number];

/** `staleTime` for the viewers' `['features', layerId]` queries. */
export const FEATURES_STALE_MS = 60_000;

/**
 * Load features for the given layers, project-aware so we can fall back to the
 * offline snapshot. Tries the network first; on failure (offline) returns the
 * snapshotted project features filtered to the requested layers.
 *
 * Detection: we rely on the try/catch (network failure when offline) as the
 * correctness guarantee — `navigator.onLine === true` can be a false positive.
 * When `navigator.onLine === false` we short-circuit straight to the snapshot
 * to avoid waiting for a slow network timeout.
 */
export async function loadFeaturesByLayerOfflineAware(
  layerIds: string[],
  projectId: string,
): Promise<OfflineFeature[]> {
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;

  if (!offline) {
    try {
      const data = await execute(featuresByLayerQuery, { layerIds });
      return withProperties(data.featuresByLayer);
    } catch (err) {
      return loadFromSnapshot(layerIds, projectId, err);
    }
  }

  return loadFromSnapshot(layerIds, projectId, new Error('offline'));
}

async function loadFromSnapshot(
  layerIds: string[],
  projectId: string,
  originalError: unknown,
): Promise<OfflineFeature[]> {
  const storage = await createOfflineStorage();
  const snap = await getSnapshot<OfflineFeature[]>(storage, projectId, 'features');
  if (!snap) throw originalError; // nothing cached → surface the original error
  const wanted = new Set(layerIds);
  return withProperties(snap.filter((f) => f.layerId != null && wanted.has(f.layerId)));
}

// GeoJSON allows `properties: null`, but the 2D map and 3D scene read
// `feature.properties.X` throughout; give every feature an object.
function withProperties(features: OfflineFeature[]): OfflineFeature[] {
  return features.some((f) => f.properties == null)
    ? features.map((f) => (f.properties == null ? { ...f, properties: {} } : f))
    : features;
}
